import { useEffect, useMemo, useState } from 'react';
import { useToast } from "../ui/use-toast"
import { useDispatch, useSelector } from 'react-redux';

import Button from '../Button/Button';
import FormInput from '../FormInput/FormInput';
import { StateType } from '../../types/StateType';
import FormLabel from '../FormLabel/FormLabel';
import moment from 'moment';
import { commit_to_concept } from '../../helpers/policy-hubs/commit-to-concept';
import { proposeRetirement } from '../../helpers/policy-hubs/propose-retirement';
import { notify } from '../../helpers/notify';
import { setSelectedPolicyHubs, setShowCommitForm, setShowEditForm, setShowProposeDeleteForm } from '../../actions/policy-hubs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@radix-ui/react-tooltip';
import { InfoCircledIcon } from "@radix-ui/react-icons"

type FormDataType = {
  publish_on: any,
  effective_on: any,
  publish_on_relative: string,
  effective_on_relative: string,
}

function addRelativeTime(timestamp, relativeDifference) {
  if(relativeDifference == "tomorrow") {
    return timestamp.add(1, 'days').set('hour', 4).set('minutes', 0).set('second', 0);
  } 
  if (relativeDifference == '3days') {
    return timestamp.add(3, 'days').set('hour', 4).set('minutes', 0).set('second', 0);
  } 
  if (relativeDifference == '1week') {
    return timestamp.add(1, 'weeks').set('hour', 4).set('minutes', 0).set('second', 0);
  }

  return timestamp;
}

const PolicyHubsProposeDelete = ({
  all_policy_hubs,
  selected_policy_hubs,
  fetchHubs
}) => {
  const dispatch = useDispatch()
  const { toast } = useToast()

  const [geographyIds, setGeographyIds] = useState([]);
  const [errors, setErrors] = useState<any>({});
  const [tooltipOpen, setTooltipOpen] = useState<Record<string, boolean>>({});

  let publish_on_relative = localStorage.getItem("last_publish_on_relative_used") || "1week";
  let effective_on_relative = localStorage.getItem("last_effective_on_relative_used") || "1week";
  let publish_on = moment().add(7, 'days').format('YYYY-MM-DD 04:00');
  let stored_publish_on = localStorage.getItem("last_publish_on_absolute_used");
  if (stored_publish_on && moment(stored_publish_on) > moment()) {
    publish_on = moment(stored_publish_on).format("YYYY-MM-DD HH:mm");
  }

  let effective_on = moment().add(14, 'days').format('YYYY-MM-DD HH:mm');
  let stored_effective_on = localStorage.getItem("last_effective_on_absolute_used");
  if (stored_effective_on && moment(stored_effective_on) > moment()) {
    effective_on = moment(stored_effective_on).format("YYYY-MM-DD HH:mm");
        
    // if the current time is greater then the latest publish_on time but smaller then the latest effective_on time the effective on time is used as publish_on time.
    // This makes it easier for municipalities to use the same effective_on_time for changes that are not made on the same day.
    if (publish_on > effective_on) {
      publish_on = effective_on;
    }
  }

  const [formData, setFormData] = useState<FormDataType>({
    publish_on: publish_on,
    publish_on_relative: publish_on_relative,
    effective_on: effective_on,
    effective_on_relative: effective_on_relative
  });
  
  const publicationTimes = useMemo(() => {
    let publish_on = moment(formData.publish_on);
    if (formData.publish_on_relative != "other") {
      publish_on = addRelativeTime(moment(), formData.publish_on_relative);
    }

    let effective_on = moment(formData.effective_on);
    if (formData.effective_on_relative != "other") {
      effective_on = addRelativeTime(moment(publish_on), formData.effective_on_relative);
    }

    return {
      publish_on: publish_on.format('dddd D MMMM HH:mm'),
      publish_on_iso: publish_on.format(),
      effective_on: effective_on.format('dddd D MMMM HH:mm'),
      effective_on_iso: effective_on.format()
    }
  }, [formData]);

  const token = useSelector((state: StateType) => (state.authentication.user_data && state.authentication.user_data.token)||null)

  const active_phase = useSelector((state: StateType) => state.policy_hubs ? state.policy_hubs.active_phase : '');

  // If selected policy hubs changes: Load data of hub
  useEffect(() => {
    if(! selected_policy_hubs || selected_policy_hubs.length === 0) return;

    // Load hub data
    fetchGeographyIds(selected_policy_hubs);
  }, [
    selected_policy_hubs,
    selected_policy_hubs.length
  ]);

  // Find hub data in array with all policy hubs
  const fetchGeographyIds = async (zone_ids) => {
    const foundHub = all_policy_hubs.filter(x => x.geography_id && zone_ids.indexOf(x.zone_id) > -1).map(x => x.geography_id);
    if(foundHub) setGeographyIds(foundHub);
  }

  const postCommitCallback = (zone_id?: number) => {
    // Deselect hub (close action modal)
    dispatch(setSelectedPolicyHubs([]));
    // Refetch hubs (for removing the changed hub from the map)
    fetchHubs();
  }

  const deleteAndCommitToConcept = async () => {
    try {
      const response = await proposeRetirement(token, geographyIds);

      if(response && response.detail) {
        // Give error if something went wrong
        notify(
          toast,
          'Er ging iets fout bij het voorstellen tot verwijderen',
          {
            title: 'Er ging iets fout',
            variant: 'destructive'
          }
        );
      }
      else {
        commitToConcept();
      }
    } catch(err) {
      console.error('Delete error', err);
    }
  }

  const commitToConcept = async () => {
    if(! geographyIds) return;
    if(! formData.publish_on) {
      setErrors({
        publish_on: true
      });
      return;
    }
    if(! formData.effective_on) {
      setErrors({
        effective_on: true
      });
      return;
    }
    let update_text = `De zone${geographyIds.length > 1 ? 's': ''} ${geographyIds.length > 1 ? 'zijn' : 'is'} vastgesteld en omgezet naar fase: Vastgesteld concept`;

    let publish_on = publicationTimes.publish_on_iso;
    if (formData.publish_on_relative == "now") {
      publish_on = moment().add(5, 'seconds').format();
      update_text = `De zone${geographyIds.length > 1 ? 's': ''} ${geographyIds.length > 1 ? 'zijn' : 'is'} gepubliceerd en omgezet naar fase: Definitief gepubliceerd`
    }

    let effective_on = publicationTimes.effective_on_iso;
    if (formData.publish_on_relative == "now" && formData.effective_on_relative == "now") {
      effective_on = publish_on;
      update_text = `De zone${geographyIds.length > 1 ? 's': ''} ${geographyIds.length > 1 ? 'zijn' : 'is'} gearchiveerd en daarmee niet meer zichtbaar op de kaart`
    }

    const result = await commit_to_concept(token, {
      "geography_ids": geographyIds,
      "publish_on": publish_on,
      "effective_on": effective_on
    });

    // If error: Show error
    if(result && result?.detail) {
      notify(toast, 'Opslaan mislukt: ' + result?.detail, {
        title: 'Er ging iets fout',
        variant: 'destructive'
      });
      return;
    }

    localStorage.setItem("last_publish_on_relative_used", formData.publish_on_relative);
    localStorage.setItem("last_effective_on_relative_used", formData.effective_on_relative);
    localStorage.setItem("last_publish_on_absolute_used", publish_on);
    localStorage.setItem("last_effective_on_absolute_used", effective_on);

    notify(toast, update_text);

    dispatch(setShowCommitForm(false));

    postCommitCallback();
  }

  const onChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setErrors({})
  }

  const hideProposeDeleteForm = () => {
    dispatch(setShowProposeDeleteForm(false));
  }

  if(! selected_policy_hubs) return <></>;

  return (
    <div>
      <div className={`mb-2 font-bold`}>
        Voorstel tot verwijderen van zone
      </div>

      <div className="mb-8">
        Je staat op het punt om <b>{active_phase === 'active' || active_phase === 'definitief actief'
          ? (selected_policy_hubs.length === 1 ? '1 definitieve zone' : `${selected_policy_hubs.length} definitieve zones`)
          : active_phase === 'published' || active_phase === 'definitief gepubliceerd'
            ? (selected_policy_hubs.length === 1 ? '1 gepubliceerde zone' : `${selected_policy_hubs.length} gepubliceerde zones`)
            : (selected_policy_hubs.length === 1 ? '1 concept' : `${selected_policy_hubs.length} concepten`)}</b> te gaan verwijderen. Stel nu de gewenste publicatie- en verwijderdatum in.
      </div>
      <div>
        <FormLabel classes="mt-2 mb-2 font-bold">
          Wanneer publiceren?
          <TooltipProvider delayDuration={500}>
            <Tooltip open={tooltipOpen.publish} onOpenChange={(open) => setTooltipOpen({ ...tooltipOpen, publish: open })}>
              <TooltipTrigger
                onClick={(e) => {
                  e.stopPropagation();
                  setTooltipOpen({ ...tooltipOpen, publish: !tooltipOpen.publish });
                }}
              >
                <InfoCircledIcon className="inline-block ml-1 h-4 w-4 hover:text-[#15AEEF]" />
              </TooltipTrigger>
              <TooltipContent 
                side="top"
                align="center"
                className="max-w-[300px] text-sm whitespace-normal text-left p-2 bg-[#15AEEF] rounded-sm"
              >
                <p className="text-sm leading-tight font-normal text-white">
                  <b>Definitief gepubliceerd</b>
                  <br />
                  Vanaf deze datum is de verwijdering van deze zone gepubliceerd. Aanbieders kunnen deze gaan verwerken in hun servicegebied.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </FormLabel>

        <div className="
          flex
          rounded-lg bg-white
          border-solid
          border
          border-gray-400
          text-sm
          mt-2 mb-2
        ">
          {[
            {name: 'now', title: 'Nu'},
            {name: 'tomorrow', title: 'Morgen'},
            {name: '3days', title: '+3 dagen'},
            {name: '1week', title: '+1 week'},
            {name: 'other', title: 'Aangepast'},
          ].map(x => {
            return <div className={`
              ${formData.publish_on_relative === x.name ? 'Button-blue' : 'text-gray-500'}
              cursor-pointer
              flex-1
              rounded-lg
              text-center
              border-gray-500
              h-10
              flex
              flex-col
              justify-center
            `}
            key={x.name}
            onClick={() => {
              setFormData({
                ...formData,
                publish_on_relative: x.name 
              });
            }}
          >
            {x.title}
          </div>
        })}
        </div>
        
        {errors.publish_on && <p className="text-sm text-red-600 -mt-2 mb-2 font-bold">
          Vul een valide publicatiedatum/-tijd in:
        </p>}

        {formData.publish_on_relative == "other" ?
          <FormInput
            type="datetime-local"
            name="publish_on"
            value={formData.publish_on}
            onChange={onChange}
            classes="w-full"
          />
          : <div className="text-sm"> {publicationTimes.publish_on}</div>
        }
      </div>
      <div>
        <FormLabel classes="mt-8 mb-2 font-bold">
          Verwijderen na publicatie
          <TooltipProvider delayDuration={500}>
            <Tooltip open={tooltipOpen.effective} onOpenChange={(open) => setTooltipOpen({ ...tooltipOpen, effective: open })}>
              <TooltipTrigger
                onClick={(e) => {
                  e.stopPropagation();
                  setTooltipOpen({ ...tooltipOpen, effective: !tooltipOpen.effective });
                }}
              >
                <InfoCircledIcon className="inline-block ml-1 h-4 w-4 hover:text-[#15AEEF]" />
              </TooltipTrigger>
              <TooltipContent 
                side="top"
                align="center"
                className="max-w-[300px] text-sm whitespace-normal text-left p-2 bg-[#15AEEF] rounded-sm"
              >
                <p className="text-sm leading-tightt font-normal text-white">
                  <b>Definitief actief</b>
                  <br />
                  Vanaf deze datum is deze verwijdering 'Definitief actief' en moet de aanbieder deze verwerkt hebben in het servicegebied.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </FormLabel>

        <div className="
          flex
          rounded-lg bg-white
          border-solid
          border
          border-gray-400
          text-sm
          mt-2 mb-2
        ">
          {[
            {name: 'now', title: 'Meteen'},
            {name: 'tomorrow', title: '+1 dag'},
            {name: '3days', title: '+3 dagen'},
            {name: '1week', title: '+1 week'},
            {name: 'other', title: 'Aangepast'},
          ].map(x => {
            return <div className={`
              ${formData.effective_on_relative === x.name ? 'Button-blue' : 'text-gray-500'}
              cursor-pointer
              flex-1
              rounded-lg
              text-center
              border-gray-500
              h-10
              flex
              flex-col
              justify-center
            `}
            key={x.name}
            onClick={() => {
              setFormData({
                ...formData,
                effective_on_relative: x.name 
              });
            }}
          >
            {x.title}
          </div>
        })}
        </div>

        {errors.effective_on && <p className="text-sm text-red-600 -mt-2 mb-2 font-bold">
          Vul een valide activeringsdatum/-tijd in:
        </p>}

        {formData.effective_on_relative == "other" ?
          <FormInput
            type="datetime-local"
            name="effective_on"
            value={formData.effective_on}
            onChange={onChange}
            classes="w-full"
          />
          : <div className="text-sm"> {publicationTimes.effective_on} </div>
        }
      </div>
      <div className="flex w-full justify-between">
        <Button
          theme="white"
          style={{marginLeft: 0}}
          onClick={hideProposeDeleteForm}
        >
          Sluiten
        </Button>
        <Button
          theme={`greenHighlighted`}
          style={{marginRight: 0}}
          onClick={deleteAndCommitToConcept}
        >
          Stel verwijdering vast
        </Button>
      </div>
    </div>
  )
}

export default PolicyHubsProposeDelete;
